import React, { useState, useEffect, useRef } from 'react';
import { Upload, Trash2, Download, Image, FileText, File, Film } from 'lucide-react';
import api from '../../api';

function fmtSize(b) {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b/1024).toFixed(0)} KB`;
  return `${(b/1048576).toFixed(1)} MB`;
}

function FileIcon({ tipo }) {
  if (!tipo) return <File size={22} color="var(--text-dim)"/>;
  if (tipo.startsWith('image/')) return <Image size={22} color="var(--cyan)"/>;
  if (tipo.includes('pdf')) return <FileText size={22} color="var(--red)"/>;
  if (tipo.startsWith('video/')) return <Film size={22} color="var(--purple)"/>;
  return <File size={22} color="var(--text-dim)"/>;
}

export default function AssetsSection({ clienteId }) {
  const [assets, setAssets] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const load = () =>
    api.get(`/cliente_assets/${clienteId}`).then(r => setAssets(r.data)).catch(() => {});

  useEffect(() => { load(); }, [clienteId]);

  const uploadFiles = async (files) => {
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      try {
        const fd = new FormData(); fd.append('file', file);
        const up = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        await api.post(`/cliente_assets/${clienteId}`, {
          nome: file.name, url: up.data.url, tipo: file.type, dimensione: file.size,
        });
      } catch { /* errore notificato dal toast globale */ }
    }
    setUploading(false);
    load();
  };

  const onPick = async (e) => {
    await uploadFiles(Array.from(e.target.files || []));
    if (e.target) e.target.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    uploadFiles(Array.from(e.dataTransfer?.files || []));
  };
  const dragProps = {
    onDragOver: (e) => { e.preventDefault(); if (!dragOver) setDragOver(true); },
    onDragLeave: (e) => { e.preventDefault(); setDragOver(false); },
    onDrop,
  };

  const del = async (id) => {
    await api.delete(`/cliente_assets/${id}`);
    setAssets(a => a.filter(x => x.id !== id));
  };

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
        <span style={{ fontSize:14, fontWeight:600, color:'var(--text)' }}>
          Assets {assets.length > 0 && <span style={{ fontSize:12, color:'var(--text-dim)', fontWeight:400 }}>({assets.length})</span>}
        </span>
        <button onClick={() => inputRef.current?.click()} disabled={uploading} className="eden-btn-primary" style={{ fontSize:12 }}>
          <Upload size={13}/> {uploading ? 'Caricamento…' : 'Carica file'}
        </button>
        <input ref={inputRef} type="file" multiple onChange={onPick} style={{ display:'none' }}/>
      </div>

      {assets.length === 0 ? (
        <div onClick={() => inputRef.current?.click()} {...dragProps}
          style={{ border:`2px dashed ${dragOver ? 'var(--cyan)' : 'var(--border)'}`, borderRadius:12, padding:'48px 24px', textAlign:'center', cursor:'pointer', color: dragOver ? 'var(--cyan)' : 'var(--text-dim)', fontSize:13, background: dragOver ? 'var(--cyan-dim)' : 'transparent', transition:'border-color 0.15s, background 0.15s' }}>
          <Upload size={24} style={{ marginBottom:8, opacity:0.4 }}/>
          <div>{dragOver ? 'Rilascia i file qui' : (uploading ? 'Caricamento…' : 'Trascina o clicca per caricare')}</div>
          <div style={{ fontSize:11, marginTop:4 }}>Immagini, PDF, video, documenti</div>
        </div>
      ) : (
        <div {...dragProps} style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(150px,1fr))', gap:10, outline: dragOver ? '2px dashed var(--cyan)' : 'none', outlineOffset:6, borderRadius:8 }}>
          {assets.map(a => (
            <div key={a.id} style={{ border:'1px solid var(--border)', borderRadius:10, overflow:'hidden', background:'var(--bg3)' }}>
              {a.tipo?.startsWith('image/') ? (
                <div style={{ height:96, overflow:'hidden' }}>
                  <img src={a.url} alt={a.nome} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                </div>
              ) : (
                <div style={{ height:96, display:'flex', alignItems:'center', justifyContent:'center', background:'var(--surface)' }}>
                  <FileIcon tipo={a.tipo}/>
                </div>
              )}
              <div style={{ padding:'8px 10px' }}>
                <div style={{ fontSize:11.5, fontWeight:500, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:2 }} title={a.nome}>{a.nome}</div>
                <div style={{ fontSize:10, color:'var(--text-dim)', marginBottom:8 }}>{fmtSize(a.dimensione)}</div>
                <div style={{ display:'flex', gap:5 }}>
                  <a href={a.url} target="_blank" rel="noreferrer"
                    style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:3, fontSize:11, color:'var(--cyan)', border:'1px solid rgba(0,212,255,0.3)', borderRadius:6, padding:'4px 0', textDecoration:'none' }}>
                    <Download size={10}/> Apri
                  </a>
                  <button onClick={() => del(a.id)}
                    style={{ border:'1px solid var(--border)', borderRadius:6, padding:'4px 7px', background:'none', color:'var(--text-dim)', cursor:'pointer', display:'flex', alignItems:'center', transition:'color 0.15s, border-color 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.color='var(--red)'; e.currentTarget.style.borderColor='rgba(244,63,94,0.4)'; }}
                    onMouseLeave={e => { e.currentTarget.style.color='var(--text-dim)'; e.currentTarget.style.borderColor='var(--border)'; }}>
                    <Trash2 size={10}/>
                  </button>
                </div>
              </div>
            </div>
          ))}
          <div onClick={() => inputRef.current?.click()}
            style={{ border:'2px dashed var(--border)', borderRadius:10, minHeight:140, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'var(--text-dim)', fontSize:11, gap:6, transition:'border-color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor='rgba(0,212,255,0.4)'}
            onMouseLeave={e => e.currentTarget.style.borderColor='var(--border)'}>
            <Upload size={16}/>
            Aggiungi
          </div>
        </div>
      )}
    </div>
  );
}
